'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { StickyStackContext, useStickyStackTop } from '../stickyStack'

// A page's numbered module (the design system's .ops-widget): a foreground
// top rule, a heading row -- "01" in the signal colour, a chevron, the icon,
// the title at 16 px, a mono summary, actions on the right -- and a body
// under a hairline. The title folds the body; with a storageKey the fold
// is remembered. ModuleStack numbers its Modules with a CSS counter, so a
// module needs no index prop and a conditional one keeps the sequence.
//
// The heading row sticks: on a module taller than the viewport it stays
// pinned to the top of the page's scroll while its body scrolls under it, so
// you always see which widget you are in (2026-11-04, every module). It pins
// under whatever already sticks above it (the page heading) via the sticky
// stack, measures itself, and republishes stack + its own height to its
// children, so a ResizableTable's own sticky column headers stack *below* the
// pinned heading instead of under it -- the same mechanism PageHeading uses.
// stickyHeader={false} opts a module out (e.g. one nested where sticky would
// fight an ancestor's own pinned chrome).
//
// `compact` is the same grammar at panel size (13 px title, no counter):
// detail-page widgets and side panels.

export function ModuleStack({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`flex flex-col gap-10 [counter-reset:module] ${className}`}>{children}</div>
}

export function Module({
  title, icon: Icon, summary, actions, children,
  collapsed, onToggle, defaultCollapsed = false, storageKey,
  compact = false, tone, className = '', bodyClassName = '', id,
  stickyHeader = true,
}: {
  title: ReactNode
  icon?: LucideIcon
  summary?: ReactNode
  actions?: ReactNode
  children?: ReactNode
  /** Controlled fold. */
  collapsed?: boolean
  onToggle?: () => void
  /** Uncontrolled fold's initial state; storageKey remembers the choice. */
  defaultCollapsed?: boolean
  storageKey?: string
  compact?: boolean
  /** A coloured rule on the module's left edge (a detail panel's accent). */
  tone?: 'signal' | 'success' | 'warning' | 'danger'
  className?: string
  bodyClassName?: string
  id?: string
  /** The heading row stays pinned while the body scrolls under it. On by
   * default; turn off where an ancestor's own sticky chrome would fight it. */
  stickyHeader?: boolean
}) {
  const controlled = collapsed !== undefined
  const [own, setOwn] = useState<boolean>(() => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        const v = window.localStorage.getItem(`tf-module:${storageKey}`)
        if (v === '1' || v === '0') return v === '1'
      } catch { /* no storage */ }
    }
    return defaultCollapsed
  })
  const folded = controlled ? !!collapsed : own
  useEffect(() => {
    if (!storageKey || controlled) return
    try { window.localStorage.setItem(`tf-module:${storageKey}`, own ? '1' : '0') } catch { /* no storage */ }
  }, [own, storageKey, controlled])
  const toggle = () => (controlled ? onToggle?.() : setOwn((v) => !v))

  const toneRule = tone ? { signal: 'border-l-signal', success: 'border-l-success', warning: 'border-l-warning', danger: 'border-l-destructive' }[tone] : ''

  // Where the heading pins (under whatever already sticks above it), and how
  // tall it is, so children can stack below it -- see the header comment.
  const stackTop = useStickyStackTop()
  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)
  useEffect(() => {
    const el = headerRef.current
    if (!el || !stickyHeader) return
    const report = () => setHeaderHeight(el.offsetHeight)
    const ro = new ResizeObserver(report)
    ro.observe(el)
    report()
    return () => ro.disconnect()
  }, [stickyHeader])

  // A pinned header must be opaque (the body scrolls under it) and sit below
  // the page heading (z-20) yet above a table's own sticky header (z-10).
  // Its hairline moves from the body's top onto the header, so it reads as a
  // bar while pinned; folded, there is nothing to pin under, so no hairline.
  const sticky = stickyHeader
  const headerSticky = sticky ? `sticky z-[15] ${compact ? 'bg-card' : 'bg-background'} ${folded ? '' : 'border-b border-border'}` : ''

  const body = !folded && children !== undefined && (
    <div className={`${sticky ? '' : 'border-t border-border'} ${bodyClassName}`}>{children}</div>
  )

  return (
    <section
      id={id}
      className={`min-w-0 [counter-increment:module] ${compact ? `border border-border bg-card ${tone ? `border-l-2 ${toneRule}` : ''}` : 'border-t border-foreground'} ${className}`}
    >
      <div
        ref={headerRef}
        style={sticky ? { top: stackTop } : undefined}
        className={`flex items-start justify-between gap-3 ${compact ? 'min-h-[40px] px-4 py-2' : 'min-h-[58px] py-3'} ${headerSticky}`}
      >
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!folded}
          className={`flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-left transition-colors hover:text-signal ${compact ? 'text-body font-medium' : 'text-title'}`}
        >
          {!compact && (
            <span className="pt-1 font-mono text-label text-signal before:content-[counter(module,decimal-leading-zero)]" aria-hidden />
          )}
          <ChevronDown className={`h-[14px] w-[14px] shrink-0 text-signal transition-transform ${folded ? '-rotate-90' : ''}`} />
          {Icon && <Icon className={`${compact ? 'h-3.5 w-3.5' : 'h-[15px] w-[15px]'} shrink-0 text-muted-foreground`} strokeWidth={1.6} />}
          <span className="min-w-0 text-foreground">{title}</span>
          {summary && <span className="pt-[2px] font-mono text-label font-normal text-muted-foreground">{summary}</span>}
        </button>
        {actions && <div className="flex shrink-0 items-center gap-1.5 pt-1">{actions}</div>}
      </div>
      {sticky
        ? <StickyStackContext.Provider value={stackTop + headerHeight}>{body}</StickyStackContext.Provider>
        : body}
    </section>
  )
}

/** The one-line mono footer under a module's list ("12 schedules · 5 enabled · …"). */
export function ModuleFooter({ children }: { children: ReactNode }) {
  return <div className="border-t border-border px-5 py-3 font-mono text-micro leading-relaxed text-muted-foreground">{children}</div>
}
